import type { DayEntry } from '../../components/agent/scheduleDay'

export type PendingDraft = {
  periodLabel: string
  // The period's own first date, ISO: scheduleSubmissionStore.ts's own
  // submitSchedule needs it and AgentDashboardPage.tsx's own quick
  // Submit action has no other way to know which period this draft is
  // for.
  periodStart: string
  days: DayEntry[]
  // Whether the agent has submitted this to a supervisor yet. Once it is
  // 'submitted', submitSchedule (scheduleSubmissionStore.ts) has already
  // written it to the real schedule_entries and schedule_submissions
  // tables, so this only ever moves from 'draft' to 'submitted', with one
  // exception: a returned submission reopens back to 'draft' (see
  // reopeningSubmissionId below), the same way a returned leave request
  // does.
  state: 'draft' | 'submitted'
  // Set only while this draft is standing in for a real returned
  // submission being edited back toward resubmission
  // (AgentCopySchedulePage.tsx's own `reopen` search param): its id,
  // rather than a plain boolean, since Submit needs it to call
  // reopenSchedule (scheduleSubmissionStore.ts) instead of submitSchedule.
  // Absent for an ordinary copied draft that has never been submitted.
  reopeningSubmissionId?: string
}

const STORAGE_KEY = 'muster.agent.pendingDraft'

// Keyed by the authenticated agent, not just the origin: sessionStorage
// survives a sign-out and a different agent signing in on the same tab, so
// a shared key would let the second agent open and overwrite the first
// agent's draft. Scoping the key means each agent only ever reads and
// writes their own entry.
function storageKey(userId: string): string {
  return `${STORAGE_KEY}:${userId}`
}

// Save Draft itself is still session-only: a real submission
// (scheduleSubmissionStore.ts's own submitSchedule) is the one write that
// reaches the real schedule_entries and schedule_submissions tables, per
// the product owner's own scoping. sessionStorage still has to hold a
// draft somewhere durable rather than discarding the built preview the
// moment the agent navigates away, which is what Save Draft promises.
//
// Returns whether the write actually landed: storage can be unavailable
// (private browsing, a full quota), and the caller needs to know when that
// happens so it can say so instead of confirming a save that did not
// happen.
export function savePendingDraft(userId: string, draft: PendingDraft): boolean {
  try {
    sessionStorage.setItem(storageKey(userId), JSON.stringify(draft))
    return true
  } catch {
    return false
  }
}

// The other half of the round trip: a write nobody ever reads back is no
// better than not writing at all. The copy screen reads this back on
// mount, so re-opening it after Save Draft shows the agent what they
// actually saved instead of a blank slate.
export function getPendingDraft(userId: string): PendingDraft | null {
  try {
    const raw = sessionStorage.getItem(storageKey(userId))
    if (!raw) return null
    const parsed = JSON.parse(raw) as PendingDraft
    // A draft saved before submission existed carries no state field; it
    // was always a draft, so that is the only sound default for it.
    return parsed.state ? parsed : { ...parsed, state: 'draft' }
  } catch {
    return null
  }
}

// Marks the saved draft submitted in place, including each day it holds
// (buildPreview stamps every day 'draft' the same way this undoes): the
// agent still sees exactly what they built, just no longer labeled as a
// draft. Called only after submitSchedule (scheduleSubmissionStore.ts)
// has already landed the real write; there is no reopen once it has,
// the same way a decided leave request has none. Returns false the same
// way savePendingDraft does: no draft to submit, or storage would not
// take the write.
export function submitPendingDraft(userId: string): boolean {
  const current = getPendingDraft(userId)
  if (!current) return false
  return savePendingDraft(userId, {
    ...current,
    state: 'submitted',
    days: current.days.map((day) => ({ ...day, state: 'submitted' })),
  })
}
